import type { IntlShape } from "react-intl";
import z, {
  array,
  date,
  literal,
  number,
  object,
  string,
  union,
  null as zodNull,
  ZodIssueCode,
  coerce,
} from "zod";

import {
  MAX_TOTAL_ROOMS,
  MAX_TOTAL_VISITORS,
} from "@/features/hotels/utils/constants";
import { getTotalVisitors } from "@/features/hotels/utils/rooms";
import { MIN_ADULT_AGE } from "@/utils/constants";
import { citySchema } from "@/utils/locations";

export const getDatesValidationSchema = (intl: IntlShape) =>
  object({
    checkIn: date({
      required_error: intl.formatMessage({
        defaultMessage: "Please select check In date",
        description: "Validation error shown when check-in date is missing",
      }),
    }),
    checkOut: date({
      required_error: intl.formatMessage({
        defaultMessage: "Please select check Out date",
        description: "Validation error shown when check-out date is missing",
      }),
    }),
  }).superRefine((values, ctx) => {
    const { checkIn, checkOut } = values;

    if (checkOut < checkIn) {
      ctx.addIssue({
        code: "custom",
        path: ["checkOut"],
        message: intl.formatMessage({
          defaultMessage: "Check Out date cannot be set before Check In",
          description:
            "Validation error when check-out date is set before check-in date",
        }),
      });
    }
  });

export const getRoomsValidationSchema = (intl: IntlShape) =>
  array(
    object({
      adults: number().min(1),
      children: number().min(0),
      childrenAges: array(
        number({
          required_error: intl.formatMessage({
            defaultMessage: "Specify age of the child",
            description: "Validation error when no child age is entered",
          }),
        })
          .int()
          .min(0)
          .max(MIN_ADULT_AGE - 1),
      ),
    }),
  )
    .min(
      1,
      intl.formatMessage({
        defaultMessage: "Choose at least 1 room",
        description: "Validation error when no rooms are selected",
      }),
    )
    .max(
      MAX_TOTAL_ROOMS,
      intl.formatMessage(
        {
          defaultMessage: "Choose no more than {max} rooms",
          description:
            "Validation error when number of rooms exceeds the limit",
        },
        { max: MAX_TOTAL_ROOMS },
      ),
    )
    .superRefine((rooms, ctx) => {
      const totalGuests = getTotalVisitors(rooms);
      if (totalGuests > MAX_TOTAL_VISITORS) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: intl.formatMessage(
            {
              defaultMessage: "Maximum {max} guests allowed across all rooms",
              description:
                "Validation error when total guests exceed the limit",
            },
            { max: MAX_TOTAL_VISITORS },
          ),
        });
      }
    });

export const getHotelSearchFormInputSchema = (intl: IntlShape) =>
  object({
    destination: citySchema.nullable(),
    dates: object({
      checkIn: coerce.date(),
      checkOut: coerce.date(),
    }),
    rooms: getRoomsValidationSchema(intl),
  });
export type HotelSearchFormInputValues = z.infer<
  ReturnType<typeof getHotelSearchFormInputSchema>
>;

export const getHotelSearchFormSchema = (intl: IntlShape) =>
  object({
    destination: union([
      object({
        id: string().min(1).regex(/^\d+$/),
        name: string().min(
          1,
          intl.formatMessage({
            defaultMessage: "Please select destination",
            description:
              "Validation error shown when no hotel destination is selected",
          }),
        ),
        code: string().optional(),
        countryName: string(),
        countryCode: string().optional(),
        type: literal("CITY"),
      }),
      zodNull(),
    ]).superRefine((v, ctx) => {
      if (v === null) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: intl.formatMessage({
            defaultMessage: "Please select destination",
            description:
              "Validation error shown when no hotel destination is selected",
          }),
        });
      }
    }),
    dates: getDatesValidationSchema(intl),
    rooms: getRoomsValidationSchema(intl),
  });

export type HotelSearchFormValues = z.infer<
  ReturnType<typeof getHotelSearchFormSchema>
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typecheck: z.input<ReturnType<typeof getHotelSearchFormSchema>> =
  {} as HotelSearchFormInputValues;
